'use client'

import React, { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Heading } from '@/components/heading'

const questions = [
  {
    question: '我不懂数据分析，能用好爱图表吗？',
    answer: '完全可以！爱图表采用AI智能分析，能够自动识别您的数据特征并推荐最适合的图表类型。无需专业知识，也能制作出专业水准的数据可视化作品。'
  },
  {
    question: '支持哪些数据格式导入？',
    answer: '目前支持Excel表格的直接上传，以及复制粘贴数据的方式。系统会自动识别和处理您的数据格式，让您专注于创作本身。'
  },
  {
    question: '制作完成的作品可以导出什么格式？',
    answer: '支持JPEG、PNG、PDF等多种主流格式导出，确保您的作品能够完美呈现在各种场景中，特别适合社交媒体分享。'
  },
  {
    question: '和传统图表工具相比，爱图表有什么优势？',
    answer: '爱图表集成了AI智能分析和设计建议，不仅能自动生成专业图表，还提供类Canva的编辑器，让您能够轻松添加文字、图片等元素，制作出完整的图文作品。'
  },
  {
    question: '支持哪些类型的图表？',
    answer: '提供30多种专业图表类型，从基础的折线图、饼图、柱状图到高级的桑基图、气泡图、雷达图等，满足各类数据可视化需求。'
  },
  {
    question: '生成的图表后续还能修改吗？',
    answer: '当然可以！所有生成的图表都支持深度定制，您可以调整颜色、字体、布局等各种细节，直到达到理想效果。'
  },
  {
    question: '适合哪些场景使用？',
    answer: '适用于商业报告、社交媒体内容、数据分析报告、演示文稿等多种场景。无论是职场汇报还是个人创作，都能轻松应对。'
  },
  {
    question: '需要安装软件吗？',
    answer: '不需要！爱图表是一个基于网页的SaaS服务，只需要浏览器就能使用，随时随地开始创作。'
  },
  {
    question: '制作完的作品能保存吗？',
    answer: '系统会自动保存您的创作过程，您可以随时回来继续编辑。同时支持将作品导出为多种格式，方便存档和分享。'
  },
  {
    question: '如何获得AI贝？',
    answer:
      '使用AI功能需要消耗爱图表的平台积分——AI贝。用户注册、邀请好友都会免费获赠一部分AI贝，购买会员则可以每月稳定获得大量AI贝。高级会员可单独购买AI贝，提供更多支持。'
  }
]

const leftQuestions = questions.filter((_, index) => index % 2 === 0)
const rightQuestions = questions.filter((_, index) => index % 2 === 1)

export function FAQs() {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)

  return (
    <div className="mx-auto my-20 w-full max-w-7xl px-4 md:px-8">
      <Heading as="h1" className="relative z-10 mx-auto mt-6 max-w-6xl py-2 text-center text-4xl font-bold text-foreground md:text-4xl lg:text-6xl">
        常见问题
      </Heading>

      <div className="mx-auto mt-20 hidden w-full max-w-6xl gap-6 md:grid md:grid-cols-2">
        <div className="flex w-full flex-col gap-6">
          {leftQuestions.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isHovered={hoveredItem === faq.question}
              onHover={() => setHoveredItem(faq.question)}
              onLeave={() => setHoveredItem(null)}
            />
          ))}
        </div>
        <div className="flex w-full flex-col gap-6">
          {rightQuestions.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isHovered={hoveredItem === faq.question}
              onHover={() => setHoveredItem(faq.question)}
              onLeave={() => setHoveredItem(null)}
            />
          ))}
        </div>
      </div>

      <div className="mx-auto mt-20 grid max-w-6xl gap-6 md:hidden">
        {questions.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isHovered={hoveredItem === faq.question}
            onHover={() => setHoveredItem(faq.question)}
            onLeave={() => setHoveredItem(null)}
          />
        ))}
      </div>
    </div>
  )
}

const FAQItem = ({
  question,
  answer,
  isHovered,
  onHover,
  onLeave
}: {
  question: string
  answer: string
  isHovered: boolean
  onHover: () => void
  onLeave: () => void
}) => {
  return (
    <div className="shadow-input min-h-[68px] w-full overflow-hidden rounded-lg bg-neutral-100" onMouseEnter={onHover} onMouseLeave={onLeave}>
      <div className="flex h-[68px] justify-between px-5 py-6">
        <div className="w-full">
          <h3 className="text-base font-medium text-neutral-700">{question}</h3>
        </div>
        <div className="relative mt-1 h-4 w-4 flex-shrink-0">
          <svg
            className={cn('absolute inset-0 h-4 w-4 transform stroke-[1.5] text-black transition-all duration-200', isHovered && 'rotate-180')}
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path d="M4.44897 10.0716L8 5.92871L11.551 10.0716" stroke="#8A8FA8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      <AnimatePresence mode="wait">
        {isHovered && (
          <motion.div
            initial={{ height: 0, padding: 0 }}
            animate={{ height: 'auto', padding: '1rem 1.25rem' }}
            exit={{ height: 0, padding: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative overflow-hidden text-base font-normal leading-[22px] text-neutral-700"
          >
            <motion.div
              className="absolute left-1/2 top-0 -translate-x-1/2 border-t"
              initial={{ width: '0' }}
              animate={{ width: '100%' }}
              exit={{ width: '0' }}
            ></motion.div>
            {answer.split('').map((line, index) => (
              <motion.span
                initial={{ opacity: 0, filter: 'blur(5px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(0px)' }}
                transition={{
                  duration: 0.2,
                  ease: 'easeOut',
                  delay: index * 0.005
                }}
                key={index}
              >
                {line}
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
